import type { TFunction } from 'i18next';

import { z } from 'zod';

export const createPersonalInfoSchema = (t: TFunction) =>
  z
    .object({
      firstName: z.string().min(1, t('Forms.PersonalInfo.errors.firstName')),
      lastName: z.string().min(1, t('Forms.PersonalInfo.errors.lastName')),
      email: z
        .string()
        .trim()
        .refine(v => v === '' || z.email().safeParse(v).success, {
          message: t('Forms.PersonalInfo.errors.email'),
        })
        .transform(v => (v === '' ? null : v))
        .nullable()
        .optional(),
      phone: z
        .string()
        .min(1, t('Forms.PersonalInfo.errors.phone'))
        .regex(
          /^(?:(?:\+|00)?[1-9]\d{6,14}|0[1-9]\d{8})$/,
          t('Forms.PersonalInfo.errors.invalid_phone'),
        ),
      hasDriverLicense: z.boolean().default(false),
      driverLicenseTypes: z
        .union([z.string(), z.null(), z.undefined()])
        .optional()
        .transform((val): string | null => (typeof val === 'string' ? val.trim() || null : null)),
      location: z.string().min(1, t('Forms.PersonalInfo.errors.location')),
      postalCode: z.string().nullable().optional(),
      jobTitle: z.string().min(1, t('Forms.PersonalInfo.errors.jobTitle')),
      isMobile: z.boolean().default(false),
      openToTraining: z.boolean().default(false),
      gender: z.string().nullable().optional().default(null),
      pictureBase64: z.string().nullable().optional(),
    })
    .refine(
      data =>
        !data.hasDriverLicense ||
        (data.driverLicenseTypes != null && data.driverLicenseTypes.trim().length > 0),
      {
        message: t('Forms.PersonalInfo.errors.driverLicenseTypesRequired'),
        path: ['driverLicenseTypes'],
      },
    );

export type PersonalInfoFormValues = z.infer<ReturnType<typeof createPersonalInfoSchema>>;
